import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { BASE_URL, V2_PATH_PREFIX } from '../../models/global-constant.model';
import { LocalStorageService } from '../helpers/local-storage.service';
import { TokenService } from '../helpers/token.service';
import { Crypto } from '../helpers/encryption-utils';
import { isPublicReportLocation } from '../helpers/public-report-route';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public baseUrl = environment.nodeApiUrl + '/' + environment.api_prefix;
  /**
   * The v2 API, which serves **only** the New Project Insight report's content endpoints — see
   * docs/backend/project-insights/FRONTEND-NEW-PROJECT-INSIGHT-2026-09-15.md. Everything else in the app (report
   * persistence, offices, library, uploads, and every other module) exists on v1 alone and 404s
   * here, which is why this is a second base rather than a change to `baseUrl`.
   */
  public baseUrlV2 = environment.nodeApiUrl + '/' + environment.api_prefix_v2;
  public url = environment.nodeApiUrl;
  public office_email_address: any = null;
  public office_emails_alerts: any = [];
  public office_emails: any = [];
  public responseCache = new Map();
  /** Throttle duplicate "blocked request" toasts (e.g. parallel calls during CORS failures). */
  private lastBlockedRequestToastAt = 0;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private toastr: ToastrService,
    private readonly tokenService: TokenService
  ) {}

  /**
   * Paths that must work without a JWT (registration, password flows, public report bootstrap).
   * Keep this list tight — everything else is skipped when no token to avoid post-logout API storms.
   */
  private readonly publicApiPathPrefixes = [
    '/auth/forget-password',
    '/open-user-tc',
    '/new-office-registration-request',
    '/data-report/public',
    '/reports/laya/agent-report',
    // Public buyer-facing External EOI form (no JWT — accessed via secure token).
    '/external-eoi/public',
  ];

  private normalizeRelativePath(urlFragment: string): string {
    const trimmed = (urlFragment || '').trim();
    const withSlash = trimmed.startsWith('/') ? trimmed : '/' + trimmed;
    return withSlash.split('?')[0];
  }

  /** Allow HTTP when there is a token, or when the route is explicitly public (unauthenticated). */
  private shouldProceedWithAuthenticatedRequest(relativePath: string): boolean {
    if (this.tokenService.getToken()) {
      return true;
    }
    // Public report viewer: the scoped viewer token lives in its own storage key (separate from the
    // login `token`, so opening a report can't corrupt a real session). On the `/report-view` path a
    // valid viewer token authorises the report's data calls (all-report-versions, the report detail,
    // intelligence, widget data, …); the auth interceptor attaches it as the Bearer.
    if (this.isPublicReportRoute() && this.tokenService.getPublicReportToken()) {
      return true;
    }
    return this.publicApiPathPrefixes.some(prefix => this.normalizeRelativePath(relativePath).startsWith(prefix));
  }

  /**
   * GETs that must be attempted on a public report link even with no token in hand, so the caller
   * gets a real HTTP response (or a real error) instead of `EMPTY`.
   *
   * `EMPTY` completes WITHOUT emitting, and the report screen subscribes with a `next`-only
   * handler — so a blocked call produced no data, no error and no log: just a permanently blank
   * page. That is the "keeps in loading mode" half of the bug. Letting the request through means a
   * genuine 401 surfaces as an error the UI can show and retry from.
   */
  private readonly publicReportGetPatterns: RegExp[] = [
    /^\/data-report\/[^/]+$/, // report detail by slug
    /^\/data-report\/all-report-versions\/[^/]+$/, // version list resolved before the detail call
    /^\/data-report\/report-widget-data\//,
    /^\/data-report\/intelligence\//,
  ];

  private shouldProceedWithPublicReportDetailGet(relativePath: string): boolean {
    if (!this.isPublicReportRoute()) {
      return false;
    }

    const path = this.normalizeRelativePath(relativePath);
    return this.publicReportGetPatterns.some(pattern => pattern.test(path));
  }

  private isPublicReportRoute(): boolean {
    return isPublicReportLocation();
  }
  getRoleId() {
    return Crypto.decrypt(this.localStorageService.GetItem('roleid'));
  }

  getUserId() {
    return Crypto.decrypt(this.localStorageService.GetItem('uid'));
  }

  getOfficeId() {
    return Crypto.decrypt(this.localStorageService.GetItem('officeid'));
  }

  getOfficeName() {
    return Crypto.decrypt(this.localStorageService.GetItem('office_name'));
  }
  getUserName() {
    return Crypto.decrypt(this.localStorageService.GetItem('user_name'));
  }
  getUserEmail() {
    return Crypto.decrypt(this.localStorageService.GetItem('user_email')) || '';
  }
  getRole() {
    return Crypto.decrypt(this.localStorageService.GetItem('role'));
  }

  /** Concurrent identical POSTs share one HTTP call; the entry is dropped as soon as it settles. */
  private inFlightPostRequests = new Map<string, Observable<any>>();

  /**
   * Report data POST.
   *
   * This used to memoize every response in `responseCache` forever, so a report re-read with
   * the same payload replayed the FIRST response for the life of the tab. That is what made
   * report totals disagree with each other and with the drill-down list (e.g. "Post = 9" in
   * one widget while another showed 8, then "corrected itself" once something perturbed the
   * payload). Report figures must always reflect the current database state, so the permanent
   * cache is gone.
   *
   * What is kept is in-flight de-duplication — the original `shareReplay(1)` intent — so a
   * widget re-render burst that fires the same request several times still hits the API once.
   * Once the response settles the key is released and the next read goes to the server.
   */
  postApi(url: string, params: any): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    const cacheKey = this.createCacheKey(url, params);

    const pending = this.inFlightPostRequests.get(cacheKey);
    if (pending) {
      return pending;
    }

    const request$ = this.http.post<any>(this.resolveUrl(url), params).pipe(
      // refCount tears the HTTP subscription down when the last subscriber leaves, so a widget
      // destroyed mid-flight cannot strand an entry in the map.
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inFlightPostRequests.delete(cacheKey)),
      catchError(err => {
        this.inFlightPostRequests.delete(cacheKey);
        this.notifyIfNetworkError(err);
        return throwError(() => err);
      })
    );

    this.inFlightPostRequests.set(cacheKey, request$);

    return request$;
  }

  /**
   * Resolves a request path against the right API version.
   *
   * A path carrying the {@link V2_PATH_PREFIX} marker goes to v2; everything else goes to v1
   * exactly as it always has. Done as a marker on the path rather than an extra argument on every
   * HTTP method deliberately: these methods have hundreds of call sites across the app, and an
   * optional positional flag on each is far more likely to be passed in the wrong position than a
   * prefix that is applied in one place and read in one place. Callers never build the marker by
   * hand — `ReportService.versionedPath()` is the only thing that adds it.
   */
  private resolveUrl(url: string): string {
    return url.startsWith(V2_PATH_PREFIX) ? this.baseUrlV2 + url.slice(V2_PATH_PREFIX.length) : this.baseUrl + url;
  }

  // Helper function to create a unique cache key based on URL and params
  private createCacheKey(url: string, params: any): string {
    const serializedParams = JSON.stringify(params); // Serialize the params object
    return `${this.resolveUrl(url)}|${serializedParams}`;
  }

  private inflightRequests = new Map<string, Observable<any>>();
  get<T>(url: string, params?: any, headers?: any): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url) && !this.shouldProceedWithPublicReportDetailGet(url)) {
      return EMPTY;
    }

    const fullUrl = this.resolveUrl(url);
    // Key by URL + params so different param sets don't share an observable.
    const key = `${fullUrl}|${this.serializeForKey(params)}|${this.serializeForKey(headers)}`;

    if (this.inflightRequests.has(key)) {
      return this.inflightRequests.get(key)! as Observable<T>;
    }

    const request$ = this.http
      .get<T>(fullUrl, {
        params: this.buildParams(params),
        headers: this.buildHeaders(headers),
      })
      .pipe(
        // refCount: true tears the source HTTP subscription down when the last
        // subscriber unsubscribes, preventing "zombie" entries in the inflight
        // map if a component is destroyed before the response arrives.
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => this.inflightRequests.delete(key)),
        catchError(err => {
          this.notifyIfNetworkError(err);
          this.inflightRequests.delete(key);
          return throwError(() => err);
        })
      );

    this.inflightRequests.set(key, request$);

    return request$;
  }

  /** Authenticated GET returning a raw file blob (e.g. email attachment download). */
  getBlob(url: string, params?: Record<string, string>, headers?: Record<string, string>): Observable<HttpResponse<Blob>> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    return this.http
      .get(BASE_URL + url, {
        params: this.buildParams(params),
        headers: this.buildHeaders(headers),
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError(err => {
          this.notifyIfNetworkError(err);
          return throwError(() => err);
        })
      );
  }

  // Stable JSON-ish serialization for cache keys; tolerates non-plain values.
  private serializeForKey(value: any): string {
    if (value == null) return '';
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  // Common POST Request
  post<T>(url: string, body?: any, headers?: any): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    return this.http
      .post<T>(this.resolveUrl(url), body, {
        headers: this.buildHeaders(headers),
      })
      .pipe(map(this.handleResponse), catchError(this.handleError));
  }

  /**
   * POST for a **read-only query**, sharing one HTTP request between identical concurrent callers.
   *
   * Several report widgets ask the same question at once — two `view` widgets on a tab both POST
   * `/get-field-values` for the same office, project and date range as the page mounts — and each
   * was issuing its own request for the same 1.42 MB answer, in the same second, every load. Same
   * dedup as {@link get}: the first caller creates the request, any caller arriving with the same
   * URL and body while it is in flight is handed that observable, and the entry is dropped as soon
   * as it settles.
   *
   * Deliberately a separate method rather than folding this into {@link post}: POST is also how
   * this app creates and updates records, and collapsing two identical writes into one would
   * silently lose the second. Only call this for endpoints that read.
   *
   * This is NOT a response cache — nothing is retained past the in-flight window, so a later
   * refresh with identical params still re-queries the server (which is why it isn't {@link
   * postApi}, whose cache would hand back the old answer).
   */
  postShared<T>(url: string, body?: any, headers?: any): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    const key = `POST|${this.resolveUrl(url)}|${this.serializeForKey(body)}|${this.serializeForKey(headers)}`;

    if (this.inflightRequests.has(key)) {
      return this.inflightRequests.get(key)!;
    }

    const request$ = this.http
      .post<T>(this.resolveUrl(url), body, {
        headers: this.buildHeaders(headers),
      })
      .pipe(
        map(this.handleResponse),
        // refCount tears the HTTP subscription down when the last subscriber leaves, so a widget
        // destroyed mid-flight (a tab switch) doesn't strand an entry in the map.
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => this.inflightRequests.delete(key)),
        catchError(err => {
          this.inflightRequests.delete(key);
          return this.handleError(err);
        })
      );

    this.inflightRequests.set(key, request$);

    return request$;
  }

  // Common PUT Request
  put<T>(url: string, body?: any, headers?: any): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    return this.http
      .put<T>(this.resolveUrl(url), body, {
        headers: this.buildHeaders(headers),
      })
      .pipe(map(this.handleResponse), catchError(this.handleError));
  }

  // Common PATCH Request
  patch<T>(url: string, body?: any, headers?: any): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    return this.http
      .patch<T>(BASE_URL + url, body, {
        headers: this.buildHeaders(headers),
      })
      .pipe(map(this.handleResponse), catchError(this.handleError));
  }

  // Common DELETE Request
  delete<T>(url: string, params?: any): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    return this.http.delete<T>(this.resolveUrl(url), { body: params }).pipe(map(this.handleResponse), catchError(this.handleError));
  }

  // Handle Response
  private handleResponse(response: any): any {
    const { status_code } = response ?? {};
    if (status_code === 401 && !isPublicReportLocation()) {
      window.location.href = '/#/login';
    }
    return response;
  }

  // Handle Error
  private handleError = (error: { error?: any; message?: string } | null | undefined) => {
    this.notifyIfNetworkError(error);
    return throwError(() => error?.error ?? error?.message ?? 'Unknown error occurred');
  };

  /**
   * One throttled toast when the request got no response at all (offline, DNS, blocked request).
   * 5xx are never shown here — DeploymentInProgressInterceptor handles them centrally, see
   * docs/DEPLOYMENT_IN_PROGRESS_TOASTER_FRONTEND.md.
   */
  private notifyIfNetworkError(error: unknown): void {
    if (!(error instanceof HttpErrorResponse) || error.status !== 0) {
      return;
    }
    const now = Date.now();
    if (now - this.lastBlockedRequestToastAt < 8000) {
      return;
    }
    this.lastBlockedRequestToastAt = now;
    this.toastr.info('Network issue, please check your connection.');
  }

  // Helper function to build headers dynamically
  private buildHeaders(headers: Record<string, string> = {}): HttpHeaders {
    let defaultHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });

    Object.keys(headers).forEach(key => {
      defaultHeaders = defaultHeaders.set(key, headers[key]);
    });

    return defaultHeaders;
  }

  // Helper function to build query params dynamically
  private buildParams(params: Record<string, string> = {}): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach(key => {
      httpParams = httpParams.set(key, params[key]);
    });

    return httpParams;
  }

  private cancelRequest$ = new Subject<void>();
  private cancelRequest1$ = new Subject<void>();
  postCancel2(url: string, body: any = {}, params: any = null): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    this.cancelRequest1$.next(); // Cancel any ongoing request
    const options = params ? { params: new HttpParams({ fromObject: params }) } : {};
    return this.http.post(this.baseUrl + url, body, options).pipe(
      takeUntil(this.cancelRequest1$), // Cancel if a new request is triggered
      map(this.handleResponse),
      catchError(this.handleError)
    );
  }

  cancelRequest2() {
    this.cancelRequest1$.next(); // Trigger cancellation
  }
  getCancel(url: string, params: any = null): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    this.cancelRequest$.next(); // Cancel any ongoing request
    const options = params ? { params: new HttpParams({ fromObject: params }) } : {};
    return this.http.get(this.baseUrl + url, options).pipe(
      takeUntil(this.cancelRequest$), // Cancel if a new request is triggered
      map(this.handleResponse),
      catchError(this.handleError)
    );
  }
  postCancel(url: string, body: any = {}, params: any = null): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    this.cancelRequest$.next(); // Cancel any ongoing request
    const options = params ? { params: new HttpParams({ fromObject: params }) } : {};
    return this.http.post(this.baseUrl + url, body, options).pipe(
      takeUntil(this.cancelRequest$), // Cancel if a new request is triggered
      map(this.handleResponse),
      catchError(this.handleError)
    );
  }

  cancelRequest() {
    this.cancelRequest$.next(); // Trigger cancellation
  }

  postpoll(url, params, headers) {
    if (!this.shouldProceedWithAuthenticatedRequest(url)) {
      return EMPTY;
    }

    let ops = { ...headers, observe: 'response' };
    return this.http.post(this.baseUrl + url, params, ops);
  }

  private inFlightRequests = new Map<string, Observable<any>>();

  public getApi(api: string): Observable<any> {
    if (!this.shouldProceedWithAuthenticatedRequest(api)) {
      return EMPTY;
    }

    const url = this.baseUrl + api;

    if (this.responseCache.has(url)) {
      return of(this.responseCache.get(url));
    }

    if (this.inFlightRequests.has(url)) {
      return this.inFlightRequests.get(url);
    }

    const request$ = this.http.get(url).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      tap(response => this.responseCache.set(url, response)),
      finalize(() => this.inFlightRequests.delete(url)),
      catchError(err => {
        this.notifyIfNetworkError(err);
        this.inFlightRequests.delete(url);
        return throwError(() => err);
      })
    );

    this.inFlightRequests.set(url, request$);

    return request$;
  }

  /** Clears in-memory HTTP caches so a new login in the same tab never reuses another session's data. */
  clearClientCaches(): void {
    this.responseCache.clear();
    this.inflightRequests.clear();
    this.inFlightRequests.clear();
    this.inFlightPostRequests.clear();
    this.office_email_address = null;
    this.office_emails_alerts = [];
    this.office_emails = [];
  }
}
